import { useMemo, useState } from 'react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Loader2Icon, RefreshCwIcon, SettingsIcon, TrophyIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'
import { acceptingVotes, closedBecause } from '#admin/features/voting/api.ts'
import { useSyncVotingEntries } from '#admin/features/voting/mutations.ts'
import { leaderboardQuery, votingSettingsQuery } from '#admin/features/voting/queries.ts'
import { LeaderboardSkeleton } from '#admin/features/voting/leaderboard-skeleton.tsx'
import { VotingSettingsDialog } from '#admin/features/voting/settings-dialog.tsx'

/**
 * The standings, and whether anybody can still change them.
 *
 * The organizer's leaderboard, not the public one: `/admin/leaderboard` answers whether or not
 * results are published, because deciding *whether* to publish requires seeing them first. The
 * public endpoint refuses with a `conflict` until `resultsPublic` is on — which is why this
 * page says, in words, who can currently see what.
 */
export const Route = createFileRoute('/_authenticated/voting/')({
  pendingComponent: LeaderboardSkeleton,
  staticData: { crumb: 'Voting' },
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(votingSettingsQuery()),
      context.queryClient.ensureQueryData(leaderboardQuery()),
    ]),
  component: VotingPage,
})

type Entry = { entryId: string; boothCode: string; displayName: string; votes: number }

function VotingPage() {
  const { data: settings } = useSuspenseQuery(votingSettingsQuery())
  const { data: board } = useSuspenseQuery(leaderboardQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const { data: manifest } = useQuery(manifestQuery())
  // A view-only role sees the standings and none of the controls. The server refuses them anyway.
  const canManage = can(session, 'voting.manage')
  const [editing, setEditing] = useState(false)
  const sync = useSyncVotingEntries()

  const open = acceptingVotes(settings)
  const total = board.items.reduce((sum, entry) => sum + entry.votes, 0)

  const columns = useMemo<ColumnDef<Entry, never>[]>(
    () => [
      {
        id: 'rank',
        header: '#',
        meta: { hideOnCard: true },
        // Position by row order, which is the server's ordering. Recomputing a rank here would
        // have to decide what a tie means, and the server already did.
        cell: ({ row }) => <span className="text-muted-foreground">{row.index + 1}</span>,
      },
      {
        accessorKey: 'boothCode',
        header: 'Stand',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.boothCode}</span>,
      },
      { accessorKey: 'displayName', header: 'Exhibitor', meta: { primary: true } },
      { accessorKey: 'votes', header: 'Votes', meta: { align: 'end' } },
    ],
    [],
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Booth competition"
        description={
          <>
            {total} vote{total === 1 ? '' : 's'} cast
          </>
        }
        actions={
          <>
            <StatusBadge tone={open ? 'success' : 'neutral'}>
              {open ? 'Open for voting' : (closedBecause(settings) ?? 'Closed')}
            </StatusBadge>
            {/*
              Stated as its own badge, because "why can't visitors see the results?" is otherwise a
              support ticket rather than a switch somebody forgot.
            */}
            <StatusBadge tone={settings.resultsPublic ? 'info' : 'warning'}>
              {settings.resultsPublic ? 'Results public' : 'Results not published'}
            </StatusBadge>
            {canManage ? (
              <>
                <Button variant="outline" disabled={sync.isPending} onClick={() => sync.mutate()}>
                  {sync.isPending ? <Loader2Icon className="animate-spin" /> : <RefreshCwIcon />}
                  {sync.isPending ? 'Syncing…' : 'Sync exhibitors'}
                </Button>
                <Button onClick={() => setEditing(true)}>
                  <SettingsIcon />
                  Settings
                </Button>
              </>
            ) : null}
          </>
        }
      />

      {canManage ? null : <ReadOnlyNotice area="the booth competition" />}

      <DataTable
        caption="Booth competition leaderboard"
        title="Leaderboard"
        columns={columns}
        data={board.items}
        getRowId={(entry) => entry.entryId}
        empty={
          <EmptyState
            icon={<TrophyIcon />}
            title="No stands are standing in the competition yet"
            description={
              canManage
                ? 'Click Sync exhibitors to enter every paid stand.'
                : 'Stands appear here when an organizer enters them into the competition.'
            }
          />
        }
      />

      {editing ? (
        <VotingSettingsDialog
          settings={settings}
          zone={manifest?.tenant.timezone ?? 'UTC'}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </div>
  )
}
